import { useState, useEffect, useContext, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useToast } from '../contexts/ToastContext';
import { AuthContext } from '../contexts/AuthContext';

export function useDashboardData() {
  const [projectGroups, setProjectGroups] = useState([]);
  const [quickTasks, setQuickTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useContext(AuthContext);
  const addToast = useToast();

  const fetchData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Fetch project groups with projects and tasks
      const { data: groupsData, error: groupsError } = await supabase
        .from('project_groups')
        .select(`
          *,
          projects!project_group_id (
            id,
            name,
            description,
            is_favorite,
            tasks!project_id (
              id,
              name,
              priority,
              created_at
            )
          )
        `)
        .eq('user_id', user.id)
        .eq('is_archived', false)
        .order('is_favorite', { ascending: false })
        .order('name', { ascending: true })
        .limit(6);

      if (groupsError) throw groupsError;

      // Fetch recent tasks for quick access
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select(`
          id,
          name,
          priority,
          created_at,
          projects!inner (
            id,
            name,
            project_groups!inner (
              id,
              name,
              user_id
            )
          )
        `)
        .eq('projects.project_groups.user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(8);

      if (tasksError) throw tasksError;

      // Fetch all projects for modal use
      const { data: projectsData, error: projectsError } = await supabase
        .from('projects')
        .select(`
          id,
          name,
          description,
          project_group_id,
          project_groups!inner (
            id,
            name,
            user_id
          )
        `)
        .eq('project_groups.user_id', user.id)
        .order('name', { ascending: true });

      if (projectsError) throw projectsError;

      setProjectGroups(groupsData || []);
      setQuickTasks(tasksData || []);
      setProjects(projectsData || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      addToast('Error loading dashboard: ' + error.message, 'danger');
    } finally {
      setLoading(false);
    }
  }, [user, addToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return {
    projectGroups,
    quickTasks,
    projects,
    loading,
    user,
    refetch: fetchData
  };
}
